import { Dispatch, FC, SetStateAction } from "react";
import { useIntl } from "../../../../../../_metronic/i18n/customUseIntl";
import { CSSClassesList } from "../../../../helpers/helperTypes";
import { processAndStageStylesV2 } from "../../../../../../_metronic/layout";
import { DsmGrid } from "@dsm-dcs/design-system-react";
import ReactHookDsmInput from "../../../../../modules/Helpers/ReactHookDsmInput2";
import { FormType } from "../../common";
import { UserProfilePrefs } from "../../../../../modules/Helpers/UserProfilePrefs";
import { unitLong } from "../../../../utils/unit-utils";
import { useFormContext } from "react-hook-form";
import { DairyV2Input, DairyV2InputContinuousGrowing } from "../../../../models/Intervention/DairyV2Intervention";
import RowTextFieldWithMetrics from "../../../../../modules/Farms/Intervention/RowControlledTextFieldWithMetrics2";
import { Spacer } from "../../../helpers/Spacer";
import EditModeButtons from "../../../helpers/EditModeButtons";
import { isFieldFilled } from "../CommonDataParts/InterventionGenericInfoDialog";

interface DairyV2InputNewAnimalsContinuousGrowingComponentProps {
  fieldItemPrefix: string;
  formType: FormType;
  defaultStageData: DairyV2InputContinuousGrowing;
  isNewAnimalsEdit: boolean;
  setIsNewAnimalsEdit: Dispatch<SetStateAction<boolean>>;
  resetEdit: () => void;
}

const DairyV2InputNewAnimalsContinuousGrowingComponent: FC<DairyV2InputNewAnimalsContinuousGrowingComponentProps> = ({
  fieldItemPrefix,
  formType,
  defaultStageData,
  isNewAnimalsEdit,
  setIsNewAnimalsEdit,
  resetEdit,
}) => {
  const intl = useIntl();
  const classes: CSSClassesList = processAndStageStylesV2();
  const userUOM = UserProfilePrefs.getInstance().getUserUnitPrefs();
  const fc = useFormContext<DairyV2Input>();

  return (
    <DsmGrid className={classes.dsmGridTwoColumnNoRowSpacingWithBorder}>
      <Spacer />
      <Spacer />
      {!isNewAnimalsEdit ? (
        <>
          {isFieldFilled(fc.getValues(`${fieldItemPrefix}.averageWeightNewAnimals`)) ? (
            <div>
              <RowTextFieldWithMetrics
                name={`${fieldItemPrefix}.averageWeightNewAnimals`}
                label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.AVERAGE_WEIGHT_NEW_ANIMALS' })}
                tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.AVERAGE_WEIGHT_NEW_ANIMALS.CONTINUOUS_GROWING.TOOLTIP' })}
                metricUnit={intl.formatMessage(
                  { id: 'SUSTELL.STAGE.DAIRY.INPUT.AVERAGE_WEIGHT_NEW_ANIMALS.ADORNMENT' },
                  { unit: userUOM?.unitBarnOutputMass ? unitLong(userUOM.unitBarnOutputMass) : 'kg' }
                )}
                baseline={defaultStageData.averageWeightNewAnimals}
                displayTypeSelect={false}
                type="number"
                disabled
              />
              <Spacer />
            </div>
          ) : (
            <ReactHookDsmInput
              name={`${fieldItemPrefix}.originalAverageWeightNewAnimals`}
              label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.AVERAGE_WEIGHT_NEW_ANIMALS' })}
              tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.AVERAGE_WEIGHT_NEW_ANIMALS.CONTINUOUS_GROWING.TOOLTIP' })}
              adornment={intl.formatMessage(
                { id: 'SUSTELL.STAGE.DAIRY.INPUT.AVERAGE_WEIGHT_NEW_ANIMALS.ADORNMENT' },
                { unit: userUOM?.unitBarnOutputMass ? unitLong(userUOM.unitBarnOutputMass) : 'kg' }
              )}
              type="number"
              defaultValue={defaultStageData.averageWeightNewAnimals ?? ''}
              disabled
            />
          )}
          {isFieldFilled(fc.getValues(`${fieldItemPrefix}.averageAge`)) ? (
            <div>
              <RowTextFieldWithMetrics
                name={`${fieldItemPrefix}.averageAge`}
                label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.AVERAGE_AGE' })}
                tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.AVERAGE_AGE.TOOLTIP' })}
                metricUnit={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.AVERAGE_AGE.ADORNMENT' })}
                baseline={defaultStageData.averageAge}
                displayTypeSelect={false}
                type="number"
                disabled
              />
              <Spacer />
            </div>
          ) : (
            <ReactHookDsmInput
              name={`${fieldItemPrefix}.originalAverageAge`}
              type="number"
              label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.AVERAGE_AGE' })}
              tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.AVERAGE_AGE.TOOLTIP' })}
              adornment={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.AVERAGE_AGE.ADORNMENT' })}
              defaultValue={defaultStageData.averageAge ?? ''}
              disabled
            />
          )}
        </>
      ) : (
        <>
          <div>
            <RowTextFieldWithMetrics
              name={`${fieldItemPrefix}.averageWeightNewAnimals`}
              label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.AVERAGE_WEIGHT_NEW_ANIMALS' })}
              tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.AVERAGE_WEIGHT_NEW_ANIMALS.CONTINUOUS_GROWING.TOOLTIP' })}
              metricUnit={intl.formatMessage(
                { id: 'SUSTELL.STAGE.DAIRY.INPUT.AVERAGE_WEIGHT_NEW_ANIMALS.ADORNMENT' },
                { unit: userUOM?.unitBarnOutputMass ? unitLong(userUOM.unitBarnOutputMass) : 'kg' }
              )}
              baseline={defaultStageData.averageWeightNewAnimals}
              disabled={false}
              type="number"
            />
            <Spacer />
          </div>
          <div>
            <RowTextFieldWithMetrics
              name={`${fieldItemPrefix}.averageAge`}
              label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.AVERAGE_AGE' })}
              tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.AVERAGE_AGE.TOOLTIP' })}
              metricUnit={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.AVERAGE_AGE.ADORNMENT' })}
              baseline={defaultStageData.averageAge}
              disabled={false}
              type="number"
            />
            <Spacer />
          </div>
        </>
      )}
      {formType !== FormType.View &&
        <EditModeButtons
          isEditing={isNewAnimalsEdit}
          onEdit={() => setIsNewAnimalsEdit(true)}
          onCancel={resetEdit}
        />}
    </DsmGrid>
  )
}

export default DairyV2InputNewAnimalsContinuousGrowingComponent;
